import { ClaimItem } from './ClaimItem';
import { useUpdateRundownClaims } from '../../hooks/useRundown';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, CheckSquare, Square, Star } from 'lucide-react';
import type { RundownItemFull, RundownClaimFull } from '@news-assist/shared';

interface ClaimSelectorProps {
  item: RundownItemFull;
  rundownId: string;
  onBack: () => void;
}

export function ClaimSelector({ item, rundownId, onBack }: ClaimSelectorProps) {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const orgPrefix = orgSlug ? `/${orgSlug}` : '';
  const updateClaims = useUpdateRundownClaims(rundownId);

  const handleToggleIncluded = (claimId: string, included: boolean) => {
    updateClaims.mutate({
      itemId: item.id,
      claims: [{ claimId, included }],
    });
  };

  const handleToggleStarred = (claimId: string, starred: boolean) => {
    updateClaims.mutate({
      itemId: item.id,
      claims: [{ claimId, starred }],
    });
  };

  const handleToggleClip = (claimId: string, isClip: boolean) => {
    updateClaims.mutate({
      itemId: item.id,
      claims: [{ claimId, isClip }],
    });
  };

  const handleSelectAll = () => {
    const allClaims = item.claims.map((c) => ({
      claimId: c.claimId,
      included: true,
    }));
    updateClaims.mutate({ itemId: item.id, claims: allClaims });
  };

  const handleSelectNone = () => {
    const allClaims = item.claims.map((c) => ({
      claimId: c.claimId,
      included: false,
    }));
    updateClaims.mutate({ itemId: item.id, claims: allClaims });
  };

  const includedCount = item.claims.filter((c) => c.included).length;
  const starredCount = item.claims.filter((c) => c.starred).length;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b bg-white sticky top-0">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={onBack}
            className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100 mr-2"
            title="Back to story browser"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h3 className="font-semibold text-gray-900 truncate flex-1">
            {item.story.title}
          </h3>
          <Link
            to={`${orgPrefix}/story/${item.storyId}`}
            className="text-gray-400 hover:text-blue-600 ml-2"
            title="View story"
          >
            <ExternalLink className="w-4 h-4" />
          </Link>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-3 text-gray-600">
            <span>{includedCount} selected</span>
            {starredCount > 0 && (
              <span className="flex items-center gap-1 text-amber-600">
                <Star className="w-3 h-3 fill-current" />
                {starredCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSelectAll}
              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
            >
              <CheckSquare className="w-3 h-3" />
              All
            </button>
            <button
              onClick={handleSelectNone}
              className="text-xs text-gray-500 hover:underline flex items-center gap-1"
            >
              <Square className="w-3 h-3" />
              None
            </button>
          </div>
        </div>
      </div>

      {/* Claims list */}
      <div className="flex-1 overflow-y-auto p-4">
        {item.claims.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No claims available for this story
          </div>
        ) : (
          <div className="space-y-2">
            {item.claims.map((claim) => (
              <ClaimItem
                key={claim.id}
                claim={claim}
                onToggleIncluded={(included) =>
                  handleToggleIncluded(claim.claimId, included)
                }
                onToggleStarred={(starred) =>
                  handleToggleStarred(claim.claimId, starred)
                }
                onToggleClip={(isClip) =>
                  handleToggleClip(claim.claimId, isClip)
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
